import React, { useState } from 'react';
import API from '../services/api';

const TicketVerificationPage = () => {
  const [reference, setReference] = useState('');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!reference.trim()) {
      setError('Please enter a booking reference');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await API.bookings.verifyTicket(reference.trim());
      setTicket(response.data);
    } catch (err) {
      setError(err.message || 'Ticket not found');
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: 'bg-green-500',
      pending: 'bg-yellow-500',
      cancelled: 'bg-red-500',
      completed: 'bg-blue-500'
    };
    return badges[status] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-40 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            🎫 Ticket Verification
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Enter booking reference to verify ticket details
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Booking Reference
              </label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value.toUpperCase())}
                placeholder="LA-20241221-XXXXX"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white text-lg font-mono focus:ring-2 focus:ring-emerald-500"
                disabled={loading}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Verify Ticket'}
            </button>
          </form>

          {error && (
            <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-400 font-medium">{error}</p>
            </div>
          )}
        </div>

        {/* Ticket Details */}
        {ticket && (
          <div className="space-y-4">
            {/* Readiness Status */}
            <div className={`rounded-lg p-6 text-center ${
              ticket.ready_for_tour 
                ? 'bg-green-500' 
                : 'bg-yellow-500'
            }`}>
              <div className="text-4xl mb-2">
                {ticket.ready_for_tour ? '✅' : '⚠️'}
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {ticket.ready_for_tour ? 'READY FOR TOUR' : 'NOT READY'}
              </h2>
              <p className="text-white text-sm">
                {ticket.ready_for_tour 
                  ? 'All requirements met' 
                  : 'Check details below'}
              </p>
            </div>

            {/* Booking Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                Booking Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Reference</p>
                  <p className="font-mono font-bold text-gray-800 dark:text-white">
                    {ticket.booking_reference}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-white text-sm font-bold ${getStatusBadge(ticket.status)}`}>
                    {ticket.status.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Customer</p>
                  <p className="font-bold text-gray-800 dark:text-white">{ticket.customer_name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{ticket.customer_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Contact</p>
                  <p className="font-bold text-gray-800 dark:text-white">{ticket.customer_phone || 'N/A'}</p>
                </div>
              </div>
            </div>

{/* Tour Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                Tour Information
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tour Name</p>
                  <p className="font-bold text-lg text-gray-800 dark:text-white">{ticket.tour_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Date</p>
                    <p className="font-bold text-gray-800 dark:text-white">{ticket.formatted_date}</p>
                    {ticket.is_today && (
                      <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">TODAY</span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Time</p>
                    <p className="font-bold text-gray-800 dark:text-white">{ticket.formatted_time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Duration</p>
                    <p className="font-bold text-gray-800 dark:text-white">{ticket.duration}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Participants</p>
                    <p className="font-bold text-gray-800 dark:text-white">{ticket.num_participants}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ATV Details - NEW SECTION */}
            {ticket.atv_details && (
              <div className="bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg shadow-lg p-6 border-2 border-blue-300 dark:border-blue-700">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <span className="text-2xl">🏍️</span>
                  ATV Configuration
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">ATV Type</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {ticket.atv_details.type_label}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total ATVs</p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {ticket.atv_details.num_atvs}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Riders</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {ticket.atv_details.num_riders}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Passengers</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {ticket.atv_details.num_passengers}
                    </p>
                  </div>
                </div>
                <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Price per ATV</p>
                  <p className="text-xl font-bold text-gray-800 dark:text-white">
                    ${ticket.atv_details.price_per_atv.toFixed(2)} USD
                  </p>
                </div>
              </div>
            )}

            {/* Payment Status */}
            <div className={`rounded-lg p-6 ${
              ticket.payment_verified 
                ? 'bg-green-100 dark:bg-green-900/30 border border-green-500' 
                : 'bg-red-100 dark:bg-red-900/30 border border-red-500'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-gray-800 dark:text-white">Payment Status</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    ${parseFloat(ticket.total_amount).toFixed(2)}
                  </p>
                </div>
                <div className="text-4xl">
                  {ticket.payment_verified ? '💳✅' : '💳❌'}
                </div>
              </div>
              <p className={`mt-2 font-bold ${
                ticket.payment_verified ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
              }`}>
                {ticket.payment_status.toUpperCase()}
              </p>
            </div>

{/* Waiver Status */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                  Waiver Status
                </h3>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-800 dark:text-white">
                    {ticket.waiver_progress.signed}/{ticket.waiver_progress.total}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Signed</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full transition-all ${
                      ticket.waiver_progress.all_signed ? 'bg-green-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${ticket.waiver_progress.percentage}%` }}
                  ></div>
                </div>
                <p className="text-center mt-2 font-bold text-gray-800 dark:text-white">
                  {ticket.waiver_progress.percentage}% Complete
                </p>
                
                {/* Status Summary */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-500 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {ticket.waiver_progress.signed}
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300 font-medium">Signed</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-500 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {ticket.waiver_progress.unsigned}
                    </p>
                    <p className="text-xs text-red-700 dark:text-red-300 font-medium">Pending</p>
                  </div>
                </div>
              </div>

              {/* ALL Participants List - Riders AND Passengers */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 text-sm mb-3">
                  All Participants ({ticket.participants.length})
                </h4>
                
                {ticket.participants.map((participant, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      participant.waiver_signed 
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-500' 
                        : 'bg-red-50 dark:bg-red-900/20 border-red-500'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Participant Info */}
                      <div className="flex-1">
                        {/* Name and Role */}
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-bold text-gray-800 dark:text-white text-lg">
                            {participant.full_name}
                          </p>
                          {participant.is_rider ? (
                            <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full font-bold inline-flex items-center gap-1">
                              <span>🏍️</span> RIDER
                            </span>
                          ) : (
                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full font-bold inline-flex items-center gap-1">
                              <span>👤</span> PASSENGER
                            </span>
                          )}
                        </div>
                        
                        {/* Contact Info */}
                        {participant.email && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {participant.email}
                          </p>
                        )}
                        
                        {participant.phone && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {participant.phone}
                          </p>
                        )}
                        
                        {participant.age && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            <span className="font-medium">Age:</span> {participant.age} years
                          </p>
                        )}
                        
                        {/* Medical Conditions Warning */}
                        {participant.medical_conditions && (
                          <div className="mt-2 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-500 rounded p-2">
                            <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium flex items-start gap-2">
                              <span className="text-lg">⚠️</span>
                              <span>
                                <strong>Medical:</strong> {participant.medical_conditions}
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Waiver Status */}
                      <div className="text-right flex-shrink-0">
                        {participant.waiver_signed ? (
                          <div className="space-y-2">
                            <div className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full font-bold shadow-lg">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              SIGNED
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {new Date(participant.waiver_signed_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500">
                              {new Date(participant.waiver_signed_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-full font-bold shadow-lg animate-pulse">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              UNSIGNED
                            </div>
                            <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                              ⚠️ Waiver Required
                            </p>
                            {participant.email && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                                Email sent to participant
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Warning if not all signed */}
              {!ticket.waiver_progress.all_signed && (
                <div className="mt-4 bg-yellow-100 dark:bg-yellow-900/30 border-2 border-yellow-500 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="font-bold text-yellow-800 dark:text-yellow-300 mb-1">
                        Action Required
                      </p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400">
                        <strong>{ticket.waiver_progress.unsigned} participant(s)</strong> still need to sign their waivers. 
                        The tour cannot proceed until all participants have signed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* All Signed Success Message */}
              {ticket.waiver_progress.all_signed && (
                <div className="mt-4 bg-green-100 dark:bg-green-900/30 border-2 border-green-500 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-bold text-green-800 dark:text-green-300">
                        ✓ All Waivers Completed
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-400">
                        All riders and passengers have signed their waivers!
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pickup Information */}
            {ticket.pickup_location && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h4 className="font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Pickup Location
                </h4>
                <p className="text-lg text-gray-800 dark:text-white font-medium">
                  {ticket.pickup_location}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  📍 Please arrive 15 minutes early
                </p>
              </div>
            )}

            {/* Special Requirements */}
            {ticket.special_requirements && (
              <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-500 rounded-lg p-6">
                <h4 className="font-bold text-gray-800 dark:text-white mb-2">
                  ℹ️ Special Requirements
                </h4>
                <p className="text-gray-700 dark:text-gray-300">{ticket.special_requirements}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketVerificationPage;